/**
 * Clients Service
 * All client-related database operations using Supabase.
 */

import { supabase } from '../../utils/supabase_config';
import { SupabaseService } from './supabaseService';
import { TABLES } from './tables';

export class ClientsService extends SupabaseService {
  /**
   * Get all clients (active + deactivated).
   */
  static async getAll() {
    return super.getAll(TABLES.CLIENTS);
  }

  /**
   * Create a new client record.
   * @param {Object} data - { name, rate }
   * @returns {Promise<string>} New row id
   */
  static async createClient(data) {
    return this.create(TABLES.CLIENTS, {
      name: data.name,
      rate: data.rate ?? { '0': 0 },
      is_deactivated: false
    });
  }

  /**
   * Update the billing rate for a client.
   * Preserves historical rate object: key "0" = current rate,
   * key = epoch ms string = historical rate.
   * @param {string} clientId
   * @param {number} newRate
   * @param {number} effectiveDate - epoch ms timestamp
   */
  static async updateRate(clientId, newRate, effectiveDate) {
    try {
      const { data, error } = await supabase
        .from(TABLES.CLIENTS)
        .select('rate')
        .eq('id', clientId)
        .single();
      if (error) throw error;

      const rateObj = { ...data.rate };
      const oldCurrentRate = rateObj['0'];

      // Archive old rate
      rateObj[String(effectiveDate)] = oldCurrentRate;
      // Set new current rate
      rateObj['0'] = newRate;

      return this.update(TABLES.CLIENTS, clientId, { rate: rateObj });
    } catch (error) {
      this.handleError('Error updating client rate', error);
      throw error;
    }
  }

  /**
   * Update client fields.
   * @param {string} clientId
   * @param {Object} updates
   */
  static async updateClient(clientId, updates) {
    return this.update(TABLES.CLIENTS, clientId, updates);
  }

  /**
   * Soft-deactivate a client.
   * @param {string} clientId
   * @param {number} deactivationTime - epoch ms
   */
  static async deactivate(clientId, deactivationTime = Date.now()) {
    return this.update(TABLES.CLIENTS, clientId, {
      is_deactivated: true,
      deactivation_time: deactivationTime
    });
  }

  /**
   * Restore a deactivated client.
   * @param {string} clientId
   */
  static async restore(clientId) {
    return this.update(TABLES.CLIENTS, clientId, {
      is_deactivated: false,
      deactivation_time: null
    });
  }

  /**
   * Hard-delete a client record.
   * @param {string} clientId
   */
  static async deleteClient(clientId) {
    return this.delete(TABLES.CLIENTS, clientId);
  }
}
